<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useTasksStore } from '@/stores/v2';
import { storeToRefs } from 'pinia';
import { useFishImport } from "@/composables/tasks/fish-import/useFishImport";
import { useTaskHistory } from "@/composables/tasks/useTaskHistory";
import { useNotifications } from "@/composables/useNotifications";
import { normalizeRouteParam } from "@/utils/v2/routes";
import TaskHeader from '@/components/v2/Tasks/Task/TaskHeader.vue';
import ModalBox from "@/components/v2/Modal/ModalBox.vue";
import FooterActions from "@/components/v2/Tasks/Vaccination/Buttons/ActionButtons/FooterActions.vue";
import JustificationModal from "@/components/v2/Modal/JustificationModal.vue";

type FishImportView = "imports" | "overview";

interface Props {
  viewMode?: FishImportView;
}

// eslint-disable-next-line no-undef
const props = defineProps<Props>();

const route = useRoute();
const router = useRouter();
const taskKey = normalizeRouteParam(route.params.key as string | string[]);
const { activeTask } = storeToRefs(useTasksStore());
const { recordChange, requireJustification } = useTaskHistory();
const { showSuccess, showError, showWarning } = useNotifications();

const {
  importData,
  transfers,
  selectedTransfer,
  selectedTransferIndex,
  selectTransfer,
  globalImportAmount,
  recordTransferAmount,
  recordTransferRemoval,
  addDiscrepancyOnTask,
  totalActualAmount,
  totalTargetAmount,
  totalAddedAmount,
  totalRemovedAmount,
  selectedTransferBreakdown,
  selectedTransferProgressEntries,
  handleCompleteTask,
  getTransferActualAmount,
  getEventTypeLabel,
  getOperationLabel,
  formatTimestamp,
  importLogModal,
  toggleImportLogModal,
} = useFishImport();

const currentView = computed<FishImportView>(() => props.viewMode || "imports");
const isImportsView = computed(() => currentView.value === "imports");
const isOverviewView = computed(() => currentView.value === "overview");
const hasSelectedTransfer = computed(() => !!selectedTransfer.value);

const getTransferLabel = (transferIndex: number): string => {
  const transfer = transfers.value[transferIndex];
  if (!transfer) return "Unknown tank";
  return `${transfer.tank.label} (${transfer.tank.room})`;
};

const pageTitle = computed(() => {
  if (isImportsView.value) return "Import Fish";
  return "Import Overview";
});
const pageDescription = computed(() => {
  if (isImportsView.value) return "Record imported fish per tank and remove fish when needed with justification.";
  return "Review all import and removal events before finishing the task.";
});
const hasTransfers = computed(() => transfers.value.length > 0);

const importDraftAmounts = ref<Record<string, number | null>>({});

const getRemainingToTarget = (transferTarget: number, importedAmount: number): number => {
  return transferTarget - importedAmount;
};

const recordImportForTransfer = (transferIndex: number, transferKey: string) => {
  const amount = Number(importDraftAmounts.value[transferKey] || 0);

  if (!Number.isFinite(amount) || amount <= 0) {
    showWarning("Invalid Amount", "Please enter an import amount greater than 0.");
    return;
  }

  selectTransfer(transferIndex);
  globalImportAmount.value = amount;
  recordTransferAmount();
  importDraftAmounts.value[transferKey] = null;
};

const recordRemovalForTransfer = async (transferIndex: number, transferKey: string) => {
  const amount = Number(importDraftAmounts.value[transferKey] || 0);

  if (!Number.isFinite(amount) || amount <= 0) {
    showWarning("Invalid Amount", "Please enter a removal amount greater than 0.");
    return;
  }

  const transfer = transfers.value[transferIndex];
  if (!transfer) {
    showError("Tank Not Found", "Unable to find selected tank for removal.");
    return;
  }

  const normalizedAmount = Math.abs(amount);
  const currentAmount = getTransferActualAmount(transfer);
  const newAmount = currentAmount - normalizedAmount;

  if (normalizedAmount > currentAmount) {
    showWarning(
      "Insufficient Imported Fish",
      `Cannot remove ${normalizedAmount} fish from ${getTransferLabel(transferIndex)} because only ${currentAmount} are available.`
    );
    return;
  }

  try {
    const justification = await requireJustification({
      fieldPath: `imports.transfers.${transfer.key}.progressData`,
      fieldName: `Fish removal (${getTransferLabel(transferIndex)})`,
      oldValue: currentAmount,
      newValue: newAmount,
      justification: "",
    });

    const trimmedJustification = justification.trim();
    if (!trimmedJustification) return;

    const historySaved = await recordChange({
      fieldPath: `imports.transfers.${transfer.key}.progressData`,
      fieldName: `Fish removal (${getTransferLabel(transferIndex)})`,
      oldValue: currentAmount,
      newValue: newAmount,
      context: "FishImport",
      contextType: "remove",
      workflowStage: "import",
      requireJustification: false,
      justification: trimmedJustification,
    });

    if (!historySaved) {
      showError("Removal Failed", "Failed to store removal justification history.");
      return;
    }

    selectTransfer(transferIndex);
    const saved = recordTransferRemoval(normalizedAmount, "other_adjustment", trimmedJustification);
    if (!saved) return;

    importDraftAmounts.value[transferKey] = null;
    showSuccess("Removal Recorded", `${normalizedAmount} fish removed from ${getTransferLabel(transferIndex)}.`);
  } catch (error) {
    // User cancelled the justification modal.
  }
};

const goToImports = () => {
  router.push({ name: "FishImportImports", params: { key: taskKey } });
};

const goToOverview = () => {
  router.push({ name: "FishImportOverview", params: { key: taskKey } });
};

// New batch ID editing state
const showBatchIdModal = ref(false);
const newBatchId = ref('');

// Weight (size) editing state
const showWeightModal = ref(false);
const newWeight = ref('');

// Show batch ID edit modal
const startEditBatchId = () => {
  newBatchId.value = importData.value?.batchId ?? "";
  showBatchIdModal.value = true;
};

// Show weight edit modal
const startEditWeight = () => {
  newWeight.value = importData.value?.size ?? "";
  showWeightModal.value = true;
};

// Close batch ID edit modal
const closeBatchIdModal = () => {
  showBatchIdModal.value = false;
};

// Close weight edit modal
const closeWeightModal = () => {
  showWeightModal.value = false;
};

// Handle save batch ID with justification
const saveBatchId = async () => {
  if (newBatchId.value) {
    const oldValue = importData.value?.batchId ?? "";

    try {
      const success = await recordChange({
        fieldPath: 'importData.batchId',
        fieldName: 'Batch ID',
        oldValue,
        newValue: newBatchId.value,
        context: 'FishImport',
        contextType: 'edit',
        workflowStage: 'import',
        requireJustification: true,
        justification: ''
      });

      if (success && importData.value) {
        importData.value.batchId = newBatchId.value;
        showSuccess("Batch ID Updated", "Batch ID has been successfully updated.");
        closeBatchIdModal();
      }
    } catch (error) {
      console.error('Error updating batch ID:', error);
      showError("Update Failed", "Failed to update batch ID. Please try again.");
    }
  }
};

// Handle save weight with justification
const saveWeight = async () => {
  if (newWeight.value) {
    const oldValue = importData.value?.size ?? "";

    try {
      const success = await recordChange({
        fieldPath: 'importData.size',
        fieldName: 'Weight',
        oldValue,
        newValue: newWeight.value,
        context: 'FishImport',
        contextType: 'edit',
        workflowStage: 'import',
        requireJustification: true,
        justification: ''
      });

      if (success && importData.value) {
        importData.value.size = newWeight.value;
        showSuccess("Weight Updated", "Weight has been successfully updated.");
        closeWeightModal();
      }
    } catch (error) {
      console.error('Error updating weight:', error);
      showError("Update Failed", "Failed to update weight. Please try again.");
    }
  }
};

// Comment for the import log modal
const comment = ref('');

// Handle submit when user adds a comment for discrepancy
const handleSubmitComment = () => {
  if (comment.value) {
    try {
      const saved = addDiscrepancyOnTask(comment.value);
      if (saved) {
        comment.value = '';
        toggleImportLogModal();
        handleCompleteTask();
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
      showError("Submission Failed", "Failed to submit comment. Please try again.");
    }
  }
};

</script>

<template>
  <div>
    <TaskHeader v-if="activeTask" :title="activeTask.title" :study="activeTask.studyTitle" />

    <div class="mt-6 space-y-4">

      <!-- </div> -->

      <!-- Imports Page -->
      <div v-if="isImportsView" class="space-y-4">
        <div v-if="importData" class="p-4 bg-green-50 rounded-lg shadow-sm ring-1 ring-green-200">
          <h3 class="text-md font-semibold text-green-900 mb-3">Import details</h3>
          <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div class="flex flex-col">
              <span class="text-xs font-semibold text-green-800 uppercase">Vendor</span>
              <span class="text-sm text-green-700">{{ importData.vendor }}</span>
            </div>
            <div class="flex flex-col">
              <span class="text-xs font-semibold text-green-800 uppercase">Batch ID</span>
              <div class="flex items-center cursor-pointer group" @click="startEditBatchId">
                <span class="text-sm text-green-700 group-hover:text-green-900 group-hover:underline">
                  {{ importData.batchId }}
                </span>
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1 text-green-600" fill="none"
                  viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </div>
            </div>
            <div class="flex flex-col">
              <span class="text-xs font-semibold text-green-800 uppercase">Weight</span>
              <div class="flex items-center cursor-pointer group" @click="startEditWeight">
                <span class="text-sm text-green-700 group-hover:text-green-900 group-hover:underline">
                  {{ importData.size }}
                </span>
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1 text-green-600" fill="none"
                  viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </div>
            </div>
            <div class="flex flex-col">
              <span class="text-xs font-semibold text-green-800 uppercase">Size Tolerance</span>
              <span class="text-sm text-green-700">{{ importData.sizeTolerance ?? '-' }}</span>
            </div>
            <div class="flex flex-col">
              <span class="text-xs font-semibold text-green-800 uppercase">Species</span>
              <span class="text-sm text-green-700">{{ importData.species }}</span>
            </div>
          </div>
        </div>

        <div v-if="!hasTransfers" class="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          No tanks are configured for this import task.
        </div>

        <div v-else class="p-4 bg-white rounded-lg shadow-sm border border-gray-200">
          <h3 class="text-md font-semibold text-gray-900 mb-1">Import by tank</h3>
          <p class="text-sm text-gray-600 mb-3">Record fish directly on each tank to avoid switching contexts.</p>

          <div class="space-y-3">
            <div v-for="(transfer, index) in transfers" :key="transfer.key"
              class="p-3 rounded-lg border border-gray-200">
              <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <p class="font-semibold text-sm text-gray-900">{{ transfer.tank.label }} ({{ transfer.tank.room }})</p>
                <p class="text-xs font-medium text-gray-700">
                  Imported {{ getTransferActualAmount(transfer) }} / {{ transfer.targetAmount }}
                </p>
              </div>

              <div class="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                <p class="text-gray-600">Target: {{ transfer.targetAmount }}</p>
                <p class="text-gray-600">Imported: {{ getTransferActualAmount(transfer) }}</p>
                <p
                  :class="getRemainingToTarget(transfer.targetAmount, getTransferActualAmount(transfer)) < 0 ? 'text-red-700 font-medium' : 'text-gray-600'">
                  Remaining: {{ getRemainingToTarget(transfer.targetAmount, getTransferActualAmount(transfer)) }}
                </p>
              </div>

              <div class="mt-3 flex flex-col sm:flex-row gap-2">
                <input type="number" v-model.number="importDraftAmounts[transfer.key]" min="1"
                  class="border border-gray-300 text-center text-base w-full p-2 rounded" placeholder="Amount" />
                <div class="sm:w-[24rem] grid grid-cols-2 gap-2">
                  <button @click="recordImportForTransfer(index, transfer.key)"
                    class="text-sm rounded bg-primary px-4 py-2 text-white border border-white"
                    :disabled="Number(importDraftAmounts[transfer.key] || 0) <= 0">
                    Add to tank
                  </button>
                  <button @click="recordRemovalForTransfer(index, transfer.key)"
                    class="text-sm rounded bg-rose-700 px-4 py-2 text-white border border-white"
                    :disabled="Number(importDraftAmounts[transfer.key] || 0) <= 0 || getTransferActualAmount(transfer) <= 0">
                    Remove from tank
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Overview Page -->
      <div v-else class="space-y-4">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div class="p-3 bg-white rounded-lg border border-gray-200">
            <p class="text-xs text-gray-500 uppercase">Target</p>
            <p class="text-lg font-semibold text-gray-900">{{ totalTargetAmount }}</p>
          </div>
          <div class="p-3 bg-white rounded-lg border border-gray-200">
            <p class="text-xs text-gray-500 uppercase">Added</p>
            <p class="text-lg font-semibold text-green-700">{{ totalAddedAmount }}</p>
          </div>
          <div class="p-3 bg-white rounded-lg border border-gray-200">
            <p class="text-xs text-gray-500 uppercase">Removed</p>
            <p class="text-lg font-semibold text-red-700">{{ totalRemovedAmount }}</p>
          </div>
          <div class="p-3 bg-white rounded-lg border border-gray-200">
            <p class="text-xs text-gray-500 uppercase">Net Imported</p>
            <p class="text-lg font-semibold text-indigo-700">{{ totalActualAmount }}</p>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div class="flex overflow-x-auto border-b border-gray-200">
            <template v-for="(transfer, index) in transfers" :key="transfer.key">
              <button @click="selectTransfer(index)" class="px-4 py-2 text-sm font-medium whitespace-nowrap" :class="selectedTransferIndex === index
                ? 'text-primary border-b-2 border-primary'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'">
                {{ transfer.tank.label }} ({{ transfer.tank.room }})
              </button>
              <div v-if="index < transfers.length - 1" class="h-6 w-px bg-gray-300 self-center"></div>
            </template>
          </div>

          <div v-if="!hasSelectedTransfer" class="text-gray-500 text-sm py-4 text-center">
            No tank selected.
          </div>

          <template v-else>
            <div v-if="selectedTransferProgressEntries.length === 0" class="text-gray-500 text-sm py-6 text-center">
              No activity recorded for this tank.
            </div>

            <div v-else class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event
                    </th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Change
                    </th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comment
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="entry in selectedTransferProgressEntries" :key="entry.key">
                    <td class="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">{{ formatTimestamp(entry.timestamp) }}
                    </td>
                    <td class="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                      {{ getEventTypeLabel(entry.eventType) }} ({{ getOperationLabel(entry.operation) }})
                    </td>
                    <td class="px-4 py-2 text-sm font-semibold whitespace-nowrap"
                      :class="entry.delta >= 0 ? 'text-green-700' : 'text-red-700'">
                      {{ entry.delta > 0 ? '+' : '' }}{{ entry.delta }}
                    </td>
                    <td class="px-4 py-2 text-sm text-gray-700">{{ entry.comment || '-' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </div>
      </div>

      <FooterActions>
        <template #start>
          <button @click="isImportsView ? null : goToImports()" :disabled="isImportsView"
            :class="{ 'opacity-60 cursor-not-allowed': isImportsView }" class="w-full">
            Import
          </button>
        </template>
        <template #end>
          <button v-if="isOverviewView" @click="handleCompleteTask" class="w-full">
            Finish task
          </button>
          <button v-else @click="goToOverview" class="w-full">
            Overview
          </button>
        </template>
      </FooterActions>
    </div>

    <!-- Import Log Modal -->
    <ModalBox v-if="importLogModal" @close="toggleImportLogModal" width="lg">
      <template #header>
        <h3 class="text-lg font-medium text-gray-900">
          Import Log
        </h3>
      </template>
      <template #main>
        <div class="mb-4">
          <p class="text-gray-700">
            The number of fish you imported doesn't match the expected amount.
            Please provide an explanation. This will be saved as an explicit discrepancy adjustment event:
          </p>
        </div>
        <textarea v-model="comment" class="w-full p-2 border border-gray-300 rounded-md" rows="4"
          placeholder="Enter your explanation here..."></textarea>
      </template>
      <template #footer>
        <div class="mt-4 flex justify-end">
          <button @click="handleSubmitComment"
            class="bg-primary text-white px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="!comment">
            Submit and Complete
          </button>
        </div>
      </template>
    </ModalBox>

    <!-- Batch ID Edit Modal -->
    <ModalBox v-if="showBatchIdModal" @close="closeBatchIdModal" width="md">
      <template #header>
        <h3 class="text-lg font-medium text-gray-900">
          Edit Batch ID
        </h3>
      </template>
      <template #main>
        <div class="mb-4">
          <p class="text-gray-700 mb-2">
            Current Batch ID: <span class="font-medium">{{ importData?.batchId }}</span>
          </p>
          <label for="newBatchId" class="block text-sm font-medium text-gray-700">New Batch ID</label>
          <input id="newBatchId" type="text" v-model="newBatchId"
            class="w-full p-2 border border-gray-300 rounded-md mt-1" placeholder="Enter new Batch ID" />
        </div>
      </template>
      <template #footer>
        <div class="mt-4 flex justify-end space-x-3">
          <button @click="closeBatchIdModal" class="bg-gray-200 text-gray-800 px-4 py-2 rounded-md">
            Cancel
          </button>
          <button @click="saveBatchId"
            class="bg-primary text-white px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="!newBatchId || newBatchId === importData?.batchId">
            Save Changes
          </button>
        </div>
      </template>
    </ModalBox>

    <!-- Weight Edit Modal -->
    <ModalBox v-if="showWeightModal" @close="closeWeightModal" width="md">
      <template #header>
        <h3 class="text-lg font-medium text-gray-900">
          Edit Weight
        </h3>
      </template>
      <template #main>
        <div class="mb-4">
          <p class="text-gray-700 mb-2">
            Current Weight: <span class="font-medium">{{ importData?.size }}</span>
          </p>
          <label for="newWeight" class="block text-sm font-medium text-gray-700">New Weight</label>
          <input id="newWeight" type="text" v-model="newWeight"
            class="w-full p-2 border border-gray-300 rounded-md mt-1" placeholder="Enter new Weight" />
        </div>
      </template>
      <template #footer>
        <div class="mt-4 flex justify-end space-x-3">
          <button @click="closeWeightModal" class="bg-gray-200 text-gray-800 px-4 py-2 rounded-md">
            Cancel
          </button>
          <button @click="saveWeight"
            class="bg-primary text-white px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="!newWeight || newWeight === importData?.size">
            Save Changes
          </button>
        </div>
      </template>
    </ModalBox>

    <!-- Justification Modal -->
    <JustificationModal />
  </div>
</template>

<style scoped>
button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
